import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Globe,
  CheckCircle,
  Loader2,
  AlertCircle,
  Brain,
  Target,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import ProfileSetupContent from '../components/profile/ProfileSetupContent';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../services/api';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
];

const totalSteps = 3;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState(user?.preferred_language || 'en');
  const [profileSummary, setProfileSummary] = useState(null);

  const selectedLanguage = useMemo(
    () => LANGUAGES.find((language) => language.code === preferredLanguage) || LANGUAGES[0],
    [preferredLanguage]
  );

  const derivedCareerGoal = profileSummary?.finalRoleTitle?.trim() || user?.career_goal || 'Full Stack Developer';
  const derivedSkills = useMemo(() => {
    const reviewedSkills = Array.isArray(profileSummary?.profileSkills)
      ? profileSummary.profileSkills
          .map((skill) => skill?.skill_name || skill?.name || '')
          .map((skill) => skill.trim())
          .filter(Boolean)
      : [];

    if (reviewedSkills.length > 0) return reviewedSkills;

    return Array.isArray(profileSummary?.sourceSkills)
      ? profileSummary.sourceSkills.map((skill) => String(skill || '').trim()).filter(Boolean)
      : [];
  }, [profileSummary]);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((step) => step + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1 && !isLoading) {
      setCurrentStep((step) => step - 1);
    }
  };

  const handleProfileProceed = (summary) => {
    setProfileSummary(summary);
    setCurrentStep(3);
  };

  const handleComplete = async () => {
    if (!profileSummary?.hasFinalRole || !derivedCareerGoal) {
      toast.error('Please complete your profile and choose a final role before continuing.');
      return;
    }

    try {
      setIsLoading(true);
      await settingsAPI.updateProfile({
        preferred_language: preferredLanguage,
      });

      updateUser?.({
        onboarding_completed: true,
        preferred_language: preferredLanguage,
        career_goal: derivedCareerGoal,
        current_skills: derivedSkills,
      });

      toast.success('🎉 Your personalized learning roadmap is ready!');

      setTimeout(() => {
        navigate(`/role-assessment?role=${encodeURIComponent(derivedCareerGoal)}`);
      }, 300);
    } catch (error) {
      let errorMessage = 'Failed to complete setup. Please try again.';

      if (error.message.includes('Validation Error')) {
        errorMessage = 'Please check your profile details and try again.';
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        errorMessage = 'Please log in again to continue.';
        navigate('/login');
        return;
      } else if (error.message.includes('422')) {
        errorMessage = 'Please ensure the required onboarding details are available.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Choose Your Language';
      case 2:
        return 'Complete Your Profile';
      case 3:
        return 'Assessment';
      default:
        return '';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return Boolean(preferredLanguage);
      case 2:
        return false;
      case 3:
        return Boolean(profileSummary?.hasFinalRole && derivedCareerGoal);
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8">
      <div className="container max-w-6xl mx-auto px-6">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-white">Complete Your Profile</h1>
            <span className="text-sm text-gray-300">Step {currentStep} of {totalSteps}</span>
          </div>

          <div className="w-full bg-slate-700 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className={currentStep === 2 ? 'min-h-[500px]' : 'bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 min-h-[500px]'}
          >
            {currentStep !== 2 && (
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-2">{getStepTitle()}</h2>
                <p className="text-gray-300">
                  {currentStep === 1 && 'Select your preferred language for the learning experience'}
                  {currentStep === 3 && 'Your profile is ready. Continue directly into the assessment step.'}
                </p>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex justify-center mb-6">
                  <Globe className="w-16 h-16 text-purple-400" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {LANGUAGES.map((language) => (
                    <button
                      key={language.code}
                      type="button"
                      onClick={() => setPreferredLanguage(language.code)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        preferredLanguage === language.code
                          ? 'border-purple-500 bg-purple-500/10 text-purple-300'
                          : 'border-slate-600 bg-slate-700/30 text-gray-300 hover:border-slate-500'
                      }`}
                    >
                      <div className="text-lg font-medium">
                        {language.flag} {language.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <ProfileSetupContent
                isEmbedded
                proceedLabel="Continue to Assessment"
                onSummaryChange={setProfileSummary}
                onProceed={handleProfileProceed}
              />
            )}

            {currentStep === 3 && (
              <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex justify-center mb-4">
                  <Brain className="w-16 h-16 text-purple-400" />
                </div>

                <div className="rounded-2xl border border-slate-700/50 bg-slate-900/50 p-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-2xl bg-purple-500/15 p-3 text-purple-300">
                      <Target className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-purple-300">Ready to assess</p>
                      <h3 className="mt-2 text-2xl font-bold text-white">{derivedCareerGoal}</h3>
                      <p className="mt-3 text-gray-300">
                        We&apos;ll use your selected language and completed profile to generate your roadmap, then send you straight into the existing assessment flow.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-700/50 bg-slate-900/50 p-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-purple-300">Preferred language</p>
                    <p className="mt-3 text-lg font-semibold text-white">
                      {selectedLanguage.flag} {selectedLanguage.name}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-700/50 bg-slate-900/50 p-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-purple-300">Reviewed skills</p>
                    <p className="mt-3 text-lg font-semibold text-white">{derivedSkills.length}</p>
                    <p className="mt-2 text-sm text-gray-400">
                      {derivedSkills.length ? derivedSkills.slice(0, 6).join(', ') : 'Your assessment will still start even if no skills were extracted.'}
                    </p>
                  </div>
                </div>

                {!profileSummary?.hasFinalRole && (
                  <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                    <div className="flex items-center text-blue-400 text-sm">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      <span>Go back and select a final role in the profile step before starting the assessment.</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between items-center mt-8">
          <Button
            variant="secondary"
            onClick={handlePrev}
            disabled={currentStep === 1 || isLoading}
            className="flex items-center bg-slate-700 hover:bg-slate-600"
          >
            <ChevronLeft size={20} className="mr-1" />
            Previous
          </Button>

          <div className="flex space-x-2">
            {Array.from({ length: totalSteps }, (_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full transition-all ${
                  index + 1 <= currentStep ? 'bg-purple-500' : 'bg-slate-600'
                }`}
              />
            ))}
          </div>

          {currentStep === 1 ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex items-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              Next
              <ChevronRight size={20} className="ml-1" />
            </Button>
          ) : currentStep === 3 ? (
            <Button
              onClick={handleComplete}
              loading={isLoading}
              disabled={!canProceed() || isLoading}
              className="flex items-center bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Preparing Assessment...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Start Assessment
                </>
              )}
            </Button>
          ) : (
            <div className="w-[146px]" />
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
